import { IMontanaFacet, IOption, IProductDimension, IProductListFacets } from "@montana/models/generated/cms.interface";
import { IProductConfigOption } from "@montana/models/dev";

interface IDimensionItem {
    name: string;
    icon: string;
}

/**
 * This function returns an object containing meta for the dimension you requested.
 * The meta data currently consists of a culture-based name and an icon for the dimension.
 *
 * Since the names are culture-based, we need to pass in the translate function from the translations container,
 * since react hooks can't be used outside a react component context
 *
 * @param translate // translate function from the translations container
 * @param dimensionName
 */
export const getDimensionMeta = (translate: Function, dimensionName?: string): IDimensionItem => {
    switch (dimensionName?.toLowerCase()) {
        case 'colour':
            return {
                name: translate((t: any) => t.productPage.dimensionColor, '[Colour]'),
                icon: 'color',
            }
        case 'base type':
            return {
                name: translate((t: any) => t.productPage.dimensionBaseType, '[Position]'),
                icon: 'position',
            }
        case 'leg finish':
            return {
                name: translate((t: any) => t.productPage.dimensionLegFinish, '[Position Colour]'),
                icon: 'frame_color',
            }
        case 'top plate':
            return {
                name: translate((t: any) => t.productPage.dimensionTopPlate, '[Top Plate]'),
                icon: 'top',
            }
        case 'measurements':
            return {
                name: translate((t: any) => t.productPage.dimensionMeasurements, '[Measurements]'),
                icon: 'specs',
            }
        case 'height':
            return {
                name: translate((t: any) => t.productPage.productHeight, '[Height]'),
                icon: 'specs',
            }
        case 'wire':
            return {
                name: translate((t: any) => t.productPage.dimensionMeasurements, '[Measurements]'),
                icon: 'specs',
            }
        case 'gallery':
            return {
                name: translate((t: any) => t.productPage.dimensionGallery, '[Gallery]'),
                icon: 'gallery',
            }
        case 'zoom':
            return {
                name: translate((t: any) => t.productPage.dimensionZoom, '[Zoom]'),
                icon: 'search',
            }
        case 'plate size':
            return {
                name: translate((t: any) => t.productPage.dimensionPlateSize, '[Plate Size]'),
                icon: 'specs',
            }
        case 'cord finish':
            return {
                name: translate((t: any) => t.productPage.dimensionCordFinish, '[Cord Finish]'),
                icon: 'chairseatcolor',
            }
        case 'frame finish':
            return {
                name: translate((t: any) => t.productPage.dimensionFrameFinish, '[Frame Finish]'),
                icon: 'chairlegcolor',
            }
        case 'finish':
            return {
                name: translate((t: any) => t.productPage.dimensionFinish, '[Finish]'),
                icon: 'color',
            }
        case 'case finish':
            return {
                name: translate((t: any) => t.productPage.dimensionCaseFinish, '[Case Finish]'),
                icon: 'base_color',
            }
        case 'topplate':
            return {
                name: translate((t: any) => t.productPage.dimensionTopPlateWithoutSpace, '[TopPlate]'),
                icon: 'top',
            }
        case 'front finish':
            return {
                name: translate((t: any) => t.productPage.dimensionFrontFinish, '[Front Finish]'),
                icon: 'front',
            }
        case 'perforated front finish':
            return {
                name: translate((t: any) => t.productPage.dimensionPerforatedFrontFinish, '[Perforated Front Finish]'),
                icon: 'color',
            }
        case 'frame':
            return {
                name: translate((t: any) => t.productPage.dimensionFrame, '[Frame]'),
                icon: 'color',
            }
        case 'plate':
            return {
                name: translate((t: any) => t.productPage.dimensionPlate, '[Plate]'),
                icon: 'color',
            }
        case 'pannels':
            return {
                name: translate((t: any) => t.productPage.dimensionPannels, '[Pannels]'),
                icon: 'color',
            }
        case 'cushion finish':
            return {
                name: translate((t: any) => t.productPage.dimensionCushionFinish, '[Cushion Finish]'),
                icon: 'color',
            }
        case 'width':
            return {
                name: translate((t: any) => t.productPage.dimensionWidth, '[width]'),
                icon: 'specs',
            }
        case 'shell finish':
            return {
                name: translate((t: any) => t.productPage.dimensionShellFinish, '[Shell Finish]'),
                icon: 'chairseatcolor',
            }
        case 'upholstery type':
            return {
                name: translate((t: any) => t.productPage.dimensionUpholsteryType, '[Upholstery Type]'),
                icon: 'chairseatcolor',
            }
        case undefined:
            return {
                name: 'No Name',
                icon: '',
            }
        default:
            return {
                name: 'Not Defined',
                icon: '',
            }
    }
};

/**
 * This function maps a dimension value with the corresponding translation.
 *
 * Since the names are culture-based, we need to pass in the translate function from the translations container,
 * since react hooks can't be used outside a react component context
 *
 * @param translate // translate function from the translations container
 * @param dimensionValueName - the value that comes from the product dimension options translationDisplayValue
 */
export const getDimensionTranslationValue = (translate: Function, dimensionValueName?: string): string | undefined => {

    switch (dimensionValueName?.toLowerCase()) {
        // You are properly admiring this lovely case, well data inconsistency and no desire to have multiple translations with same values got us here - sorry.
        case 'small':
            return translate((t: any) => t.productPage.dimensionValueSmall, '[Small]');
        case 'large':
            return translate((t: any) => t.productPage.dimensionValueLarge, '[Large]');
        case 'black':
            return translate((t: any) => t.productPage.dimensionValueColorBlack, '[Black]');
        case 'brass':
            return translate((t: any) => t.productPage.dimensionValueColorBrass, '[Brass]');
        case 'snow':
            return translate((t: any) => t.productPage.dimensionValueColorSnow, '[Snow]');
        case 'mattchrome':
        case 'matt chrome':
            return translate((t: any) => t.productPage.dimensionValueColorMattchrome, '[Matt Chrome]');
        case 'uden ophæng':
            return translate((t: any) => t.productPage.dimensionValuePositionNone, '[None]');
        case 'wall':
            return translate((t: any) => t.productPage.dimensionValuePositionWall, '[Wall]');
        case 'plinthh3':
        case 'plinth h3':
        case 'plinth-h3':
        case 'plinth 3 cm.':
            return translate((t: any) => t.productPage.dimensionValuePositionPlint3, '[Plint HH3]');
        case 'plinthh7':
        case 'plinth h7':
        case 'plinth-h7':
        case 'plinth 7 cm.':
            return translate((t: any) => t.productPage.dimensionValuePositionPlint7, '[Plint HH7]');
        case 'legs':
            return translate((t: any) => t.productPage.dimensionValuePositionLegs, '[Legs]');
        case 'castor':
            return translate((t: any) => t.productPage.dimensionValuePositionCastor, '[Castor]');
        case 'dybde20cm':
        case '20-dybde20cm':
        case 'd20-dybde20cm':
        case 'dybde30cm':
        case '30-dybde30cm':
        case 'd30-dybde30cm':
        case 'dybde38cm':
        case '38-dybde38cm':
        case 'd38-dybde38cm':
        case 'dybde468cm':
        case '46-dybde468cm':
        case 'd46-dybde468cm':
        case 'dybde1cm':
        case '1-dybde1cm':
        case 'd1-dybde1cm':
        case 'dybde376cm':
        case '37-dybde376cm':
        case 'd37-dybde376cm':
        case 'dybde354cm':
        case '35-dybde354cm':
        case 'd35-dybde354cm':
        case 'dybde25cm':
        case '25-dybde25cm':
        case 'd25-dybde25cm':
        case 'dybde32cm':
        case '32-dybde32cm':
        case 'd32-dybde32cm':
        case 'dybde60cm':
        case '60-dybde60cm':
        case 'd60-dybde60cm':
            return getDepthTranslationValue(translate, dimensionValueName);
        case 'full':
            return translate((t: any) => t.productPage.dimensionUpholsteryTypeFullValue, '[Full upholstered]')
        case 'shell':
            return translate((t: any) => t.productPage.dimensionUpholsteryTypeShell, '[Shell]')
        case 'seat and back':
            return translate((t: any) => t.productPage.dimensionUpholsteryTypeSeatBack, '[Seat and back]')
        case 'seat':
            return translate((t: any) => t.productPage.dimensionUpholsteryTypeSeat, '[Seat]')
        case 'inner side':
            return translate((t: any) => t.productPage.dimensionUpholsteryTypeInnerSide, '[Inner side]')
        default:
            return dimensionValueName;
    }
};

/**
 * This function maps a depth key value with the corresponding translation.
 * If the depthValue is not matchen the string provided is simply returned.
 *
 * Since the names are culture-based, we need to pass in the translate function from the translations container,
 * since react hooks can't be used outside a react component context
 *
 * @param translate // translate function from the translations container
 * @param depthValue
 */
export const getDepthTranslationValue = (translate: Function, depthValue?: string): string | undefined => {
    switch (depthValue?.toLowerCase()) {
        case 'dybde20cm':
        case '20-dybde20cm':
        case 'd20-dybde20cm':
            return translate((t: any) => t.productPage.dimensionValueDepth20, '[20 cm]');
        case 'dybde30cm':
        case '30-dybde30cm':
        case 'd30-dybde30cm':
            return translate((t: any) => t.productPage.dimensionValueDepth30, '[30 cm]');
        case 'dybde38cm':
        case '38-dybde38cm':
        case 'd38-dybde38cm':
            return translate((t: any) => t.productPage.dimensionValueDepth38, '[38 cm]');
        case 'dybde468cm':
        case '46-dybde468cm':
        case 'd46-dybde468cm':
            return translate((t: any) => t.productPage.dimensionValueDepth46, '[46 cm]');
        case 'dybde1cm':
        case '1-dybde1cm':
        case 'd1-dybde1cm':
            return translate((t: any) => t.productPage.dimensionValueDepth01, '[1 cm]');
        case 'dybde376cm':
        case '37-dybde376cm':
        case 'd37-dybde376cm':
            return translate((t: any) => t.productPage.dimensionValueDepth37, '[37 cm]');
        case 'dybde354cm':
        case '35-dybde354cm':
        case 'd35-dybde354cm':
            return translate((t: any) => t.productPage.dimensionValueDepth35, '[35 cm]');
        case 'dybde25cm':
        case '25-dybde25cm':
        case 'd25-dybde25cm':
            return translate((t: any) => t.productPage.dimensionValueDepth25, '[25 cm]');
        case 'dybde32cm':
        case '32-dybde32cm':
        case 'd32-dybde32cm':
            return translate((t: any) => t.productPage.dimensionValueDepth32, '[32 cm]');
        case 'dybde60cm':
        case '60-dybde60cm':
        case 'd60-dybde60cm':
            return translate((t: any) => t.productPage.dimensionValueDepth60, '[60 cm]');
        default:
            return undefined;
    }
}

export const getFacetText = (facet: IMontanaFacet, filterFacets: IProductListFacets[] | undefined): string | undefined => {
    return filterFacets?.find(f => f.facetName === facet.facetKey)?.terms?.find(t => t.filterKey === facet.facetValue)?.text;
}

/**
 * Function used to patch data shortings by using static fallback images on dimension options
 * Returns undefined when none are available.
 * @param option dimension option for which to find a fallback image.
 */
export const getStaticDimensionImage = (option: IOption): string | undefined => {
    switch (option.seoFriendlyDisplayValue) {
        case 'brass':
            return 'assets/images/montana_position_legs_brass.jpg';
        case 'snow':
            return 'assets/images/montana_position_legs_snow.jpg';
        case 'matt-chrome':
            return 'assets/images/montana_position_legs_mattchrome.jpg';
        case 'legs':
        case 'black':
            return 'assets/images/montana_position_legs_black.jpg';
        case 'rosehip':
            return 'assets/images/montana_position_legs_rosehip.jpg';
        case 'mushroom':
            return 'assets/images/montana_position_legs_mushroom.jpg';
        case 'flint':
            return 'assets/images/montana_position_legs_flint.jpg';
        case 'parsley':
            return 'assets/images/montana_position_legs_parsley.jpg';
        default:
            return;
    }
}

export const isDimensionConditionMet = (dimension: IProductDimension | IProductConfigOption, dimensions?: IProductConfigOption[]): boolean => {
    if (!dimension.condition && !dimension.invalidCondition) {
        return true;
    } else if (dimensions?.length) {
        if (dimension.condition) {
            return !!dimensions?.find(d => d.cylindoFeatureParameters === dimension.condition);
        }
        if (dimension.invalidCondition) {
            const isInvalid = !!dimensions?.find(d => d.cylindoFeatureParameters === dimension.invalidCondition);
            return !isInvalid;
        }
    }

    return false;
}

export const isOptionConditionMet = (
    option: IOption,
    currentDimensions: IProductConfigOption[] | undefined,
    currentMeasurement: string | undefined
): boolean => {
    const conds = option.conditions;

    // If no conditions → include as-is
    if (!conds?.length) {
        return true;
    }

    const measurementLower = currentMeasurement?.toLowerCase();

    return conds.some(c => {
        const conditionLower = c?.toLowerCase();

        if (conditionLower?.includes(':')) {
            const parts = conditionLower.split(':');
            const dimName = parts[0].trim();
            const dimValue = parts.slice(1).join(':').trim();

            // Handle measurements specially as they are at the top level of the config
            if (dimName === 'measurements' || dimName === 'measurement') {
                return dimValue === measurementLower;
            }

            // Look for the dimension in the current config
            const targetDimension = currentDimensions?.find(cd =>
                cd.dimensionName?.toLowerCase() === dimName ||
                cd.seoFriendlyDimensionName?.toLowerCase() === dimName
            );

            if (targetDimension) {
                const targetValues = [
                    targetDimension.seoFriendlyDisplayValue,
                    targetDimension.colorId,
                    targetDimension.id,
                    targetDimension.cylindoFeatureParameters?.includes(':')
                        ? targetDimension.cylindoFeatureParameters.split(':').slice(1).join(':')
                        : targetDimension.cylindoFeatureParameters
                ].filter(v => !!v).map(v => v!.toLowerCase().trim());

                if (targetValues.includes(dimValue)) {
                    return true;
                }
            }

            // Fallback: Check if the dimName refers to the measurement value
            return dimValue === measurementLower;
        }

        // No prefix - assume it's a measurement condition
        return conditionLower === measurementLower;
    });
}

export const isDimensionPartOfDimensions = (dimension?: string, dimensions?: string[]): boolean => {
    if (!dimension || !dimensions) { return false; }
    const match = dimensions.find(d => d === dimension);
    return !!match;
}
